import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Card } from '../components/Card'
import { InfoIcon, LearnTip } from '../lib/learn-mode'
import { fmtMoney, FMT_INT } from '../lib/money'
import { useCurrency } from '../lib/currency-context'
import { STANDARD_PERIODS, type PeriodKey } from '../lib/period-context'
import { useAuth } from '../lib/auth'
import { useTranslation } from 'react-i18next'

interface ShipKPIs {
  ships: number
  profit_cents: number
  avg_margin_cents: number
  velocity_per_day: number
  top_carrier: { name: string; ships: number }
  top_service: { name: string; avg_margin_cents: number }
  concentration: { top_n_drives_80pct: number; total_customers: number }
  delta_ships_pct: number | null
  delta_profit_pct: number | null
}

interface PivotRow { grp: string; ships: number; profit_cents: number; avg_margin_cents: number }
interface ParetoRow { customer_name: string; ships: number; profit_cents: number; cumulative_pct: number }

const GROUP_OPTIONS = [
  { key: 'service',     label: 'Service',                        labelKey: 'shipments.group_service' },
  { key: 'customer',    label: 'Customer',                       labelKey: 'shipments.group_customer' },
  { key: 'carrier',     label: 'Carrier',                        labelKey: 'shipments.group_carrier' },
  { key: 'origin',      label: 'Origin region (3-digit zip)',    labelKey: 'shipments.group_origin' },
  { key: 'destination', label: 'Destination region (3-digit zip)', labelKey: 'shipments.group_destination' },
]

const MASKED = <span className="text-ink-300 select-none">••••</span>

export function ShipmentsView() {
  const { t } = useTranslation()
  const { currency } = useCurrency()
  const { can } = useAuth()
  const [period, setPeriod] = useState<PeriodKey>('this_month')
  const [group, setGroup] = useState<string>('service')

  const kpis = useQuery<ShipKPIs>({
    queryKey: ['ships', 'kpis', period],
    queryFn: () => fetch(`/api/shipments/kpis?period=${period}`).then(r => r.json()),
  })
  const pivot = useQuery<{ rows: PivotRow[] }>({
    queryKey: ['ships', 'pivot', period, group],
    queryFn: () => fetch(`/api/shipments/pivot?period=${period}&group=${group}`).then(r => r.json()),
  })
  const pareto = useQuery<{ rows: ParetoRow[] }>({
    queryKey: ['ships', 'pareto', period],
    queryFn: () => fetch(`/api/shipments/pareto?period=${period}`).then(r => r.json()),
  })

  const k = kpis.data

  return (
    <div className="space-y-5">
      {/* Hero */}
      <Card className="p-7">
        <div className="flex items-center justify-between gap-6">
          <div>
            <div className="text-[12px] font-semibold text-ink-500 uppercase tracking-wider">
              {t('shipments.title')} <span className="text-ink-400 normal-case">— {STANDARD_PERIODS.find(p => p.key === period)?.label ?? period}</span>
              <InfoIcon tip={t('shipments.volume_margin')} />
            </div>
            <div className="mt-2 text-[44px] font-bold text-ink-900 leading-none">
              {k ? FMT_INT.format(k.ships) : <span className="text-ink-300">—</span>}
              {can('ships_profit') && (
                <span className="text-[28px] font-normal text-ink-400 ml-3">
                  → {k ? fmtMoney(k.profit_cents, currency, { compact: true }) : '—'} {t('shipments.profit')}
                </span>
              )}
            </div>
            <div className="mt-2 text-[13px] text-ink-500">
              {k && (<>
                {can('ships_profit')
                  ? <>Avg margin <b>{fmtMoney(k.avg_margin_cents, currency)}</b> per ship · </>
                  : null
                }
                velocity <b>{FMT_INT.format(k.velocity_per_day)}/day</b>
              </>)}
            </div>
            <LearnTip>{t('shipments.learn_tip')}</LearnTip>
          </div>
          <div className="text-right space-y-2">
            <select value={period} onChange={e => setPeriod(e.target.value as PeriodKey)}
                    className="px-3 py-1.5 border border-ink-200 rounded-lg text-sm bg-white text-ink-700 font-medium">
              {STANDARD_PERIODS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
            {k?.delta_ships_pct !== null && k?.delta_ships_pct !== undefined && (
              <div className="text-[12px]">
                <span className={`pill ${k.delta_ships_pct >= 0 ? 'pill-mint' : 'pill-flame'}`}>
                  {k.delta_ships_pct >= 0 ? '▲' : '▼'} {Math.abs(k.delta_ships_pct)}% vol
                </span>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* KPI strip */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="p-5">
          <div className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider">
            {t('shipments.avg_margin_per_ship')}<InfoIcon tip={t('shipments.avg_margin_tip')} />
          </div>
          <div className="mt-2 text-[24px] font-bold">
            {can('ships_profit')
              ? (k ? fmtMoney(k.avg_margin_cents, currency) : '—')
              : MASKED}
          </div>
          {can('ships_profit') && k?.delta_profit_pct !== null && k?.delta_profit_pct !== undefined && (
            <div className={`text-[11px] mt-1 font-medium ${k.delta_profit_pct >= 0 ? 'text-mint-600' : 'text-flame-600'}`}>
              {k.delta_profit_pct >= 0 ? '▲' : '▼'} {Math.abs(k.delta_profit_pct)}% profit vs prior
            </div>
          )}
          <LearnTip>{t('shipments.avg_margin_learn')}</LearnTip>
        </Card>
        <Card className="p-5">
          <div className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider">
            {t('shipments.top_carrier_volume')}<InfoIcon tip={t('shipments.top_carrier_tip')} />
          </div>
          <div className="mt-2 text-[24px] font-bold">{k?.top_carrier.name ?? '—'}</div>
          <div className="text-[11px] text-ink-500 mt-1">{k ? `${FMT_INT.format(k.top_carrier.ships)} ships` : '—'}</div>
          <LearnTip>{t('shipments.top_carrier_learn')}</LearnTip>
        </Card>
        <Card className="p-5">
          <div className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider">
            {t('shipments.top_service_margin')}<InfoIcon tip={t('shipments.top_service_tip')} />
          </div>
          <div className="mt-2 text-[16px] font-bold leading-tight">{k?.top_service.name ?? '—'}</div>
          <div className="text-[11px] text-ink-500 mt-1">
            {can('ships_profit')
              ? (k ? `${fmtMoney(k.top_service.avg_margin_cents, currency)} / ship` : '—')
              : MASKED}
          </div>
          <LearnTip>Push higher-margin services for more profit per sale.</LearnTip>
        </Card>
        <Card className="p-5">
          <div className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider">
            {t('shipments.concentration_risk')}<InfoIcon tip={t('shipments.concentration_tip')} />
          </div>
          <div className="mt-2 text-[24px] font-bold">
            {k ? `${k.concentration.top_n_drives_80pct} = 80%` : '—'}
          </div>
          <div className="text-[11px] text-ink-500 mt-1">
            {k ? `of ${k.concentration.total_customers} customers` : '—'}
          </div>
          <LearnTip>If a few customers leave, volume drops significantly. Healthier = spread across more customers.</LearnTip>
        </Card>
      </div>

      {/* Pivot table */}
      <Card className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="text-[14px] font-semibold text-ink-800">
            {t('shipments.pivot_table')}
            <InfoIcon tip={t('shipments.pivot_tip')} />
          </div>
          <select value={group} onChange={e => setGroup(e.target.value)}
                  className="px-3 py-1.5 border border-ink-200 rounded-lg text-sm bg-white text-ink-700 font-medium">
            {GROUP_OPTIONS.map(g => <option key={g.key} value={g.key}>{t(g.labelKey)}</option>)}
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-ink-500 border-b border-ink-200">
                <th className="text-left py-2 pr-3">{GROUP_OPTIONS.find(g => g.key === group) ? t(GROUP_OPTIONS.find(g => g.key === group)!.labelKey) : group}</th>
                <th className="text-right py-2 pr-3">{t('shipments.ships_col')}</th>
                {can('ships_profit') && <th className="text-right py-2 pr-3">{t('shipments.profit_col')}</th>}
                {can('ships_profit') && <th className="text-right py-2 pr-3">{t('shipments.avg_margin_col')}</th>}
              </tr>
            </thead>
            <tbody>
              {pivot.data?.rows.map(r => (
                <tr key={r.grp} className="border-b border-ink-100 hover:bg-ink-50">
                  <td className="py-2 pr-3 font-medium">{r.grp}</td>
                  <td className="py-2 pr-3 text-right num">{FMT_INT.format(r.ships)}</td>
                  {can('ships_profit') && <td className="py-2 pr-3 text-right num font-semibold">{fmtMoney(r.profit_cents, currency, { compact: true })}</td>}
                  {can('ships_profit') && <td className="py-2 pr-3 text-right num text-ink-600">{fmtMoney(r.avg_margin_cents, currency)}</td>}
                </tr>
              ))}
              {pivot.isLoading && <tr><td colSpan={can('ships_profit') ? 4 : 2} className="py-6 text-center text-ink-400">{t('common.loading')}</td></tr>}
              {pivot.data?.rows.length === 0 && <tr><td colSpan={can('ships_profit') ? 4 : 2} className="py-6 text-center text-ink-400">{t('shipments.no_data_period')}</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Pareto */}
      <Card className="p-5">
        <div className="text-[14px] font-semibold text-ink-800 mb-3">
          {t('shipments.pareto_title')}<InfoIcon tip={t('shipments.pareto_tip')} />
        </div>
        <LearnTip>{t('shipments.pareto_learn')}</LearnTip>
        <div className="overflow-x-auto mt-3">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-ink-500 border-b border-ink-200">
                <th className="text-left py-2 pr-3">Customer</th>
                <th className="text-right py-2 pr-3">{t('shipments.ships_col')}</th>
                {can('ships_profit') && <th className="text-right py-2 pr-3">{t('shipments.profit_col')}</th>}
                <th className="text-right py-2 pr-3">{t('shipments.cumulative_pct')}</th>
              </tr>
            </thead>
            <tbody>
              {pareto.data?.rows.map(r => (
                <tr key={r.customer_name} className="border-b border-ink-100 hover:bg-ink-50">
                  <td className="py-2 pr-3 font-medium">{r.customer_name}</td>
                  <td className="py-2 pr-3 text-right num">{FMT_INT.format(r.ships)}</td>
                  {can('ships_profit') && <td className="py-2 pr-3 text-right num font-semibold">{fmtMoney(r.profit_cents, currency, { compact: true })}</td>}
                  <td className="py-2 pr-3 text-right num text-ink-500">{Number(r.cumulative_pct ?? 0).toFixed(1)}%</td>
                </tr>
              ))}
              {pareto.isLoading && <tr><td colSpan={can('ships_profit') ? 4 : 3} className="py-6 text-center text-ink-400">{t('common.loading')}</td></tr>}
              {pareto.data?.rows.length === 0 && <tr><td colSpan={can('ships_profit') ? 4 : 3} className="py-6 text-center text-ink-400">{t('shipments.no_data_period')}</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  )
}